import React from 'react';
import { Link } from 'react-router-dom';
import { Anchor, ExternalLink, Hourglass, MapPin, Tag, GitCompare } from 'lucide-react';
import ActivityCardImage from './ActivityCardImage';

/**
 * One wide result row: photo left, every detail we hold in the middle, price + BOOK NOW
 * in a right hand rail. Clicking anywhere on the row opens the same booking drawer as the
 * grid cards; the charter company link and the compare checkbox are excluded from that.
 */
const ActivityListRow = ({ activity: a, mode, description, onBook, compareSelected, onToggleCompare, compareFull }) => {
  const categories = (a.category_names || []).filter(Boolean).slice(0, 3);
  // the importer seeds a tag per category, so drop tags that just repeat a category badge
  const categoryKeys = new Set(categories.map((c) => c.toLowerCase().replace(/[^a-z0-9]/g, '')));
  const tags = (a.tags || [])
    .filter(Boolean)
    .filter((t) => !categoryKeys.has(t.toLowerCase().replace(/[^a-z0-9]/g, '')))
    .slice(0, 6);
  const external = a.booking_type === 'external_link' && a.booking_provider !== 'fareharbor' && a.booking_url;

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={`Book ${a.title}`}
      onClick={(e) => onBook(a, e)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onBook(a, e); }}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition hover:shadow-md sm:flex-row"
      data-testid={`activity-row-${a.alias}`}
    >
      {/* ---------- photo: carries the logo, the duration/location and the price ---------- */}
      <div className="w-full shrink-0 self-start sm:w-56 lg:w-72">
        <ActivityCardImage activity={a} variant="list" />
      </div>

      {/* ---------- trip info ---------- */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-4">
        <h3 className="text-lg font-bold leading-snug text-gray-900 sm:text-xl">{a.title}</h3>

        {a.seller_slug && (
          <Link
            to={`/${a.seller_slug}`}
            onClick={(e) => e.stopPropagation()}
            className="w-fit text-sm font-semibold text-blue-800 underline-offset-2 hover:text-blue-900 hover:underline"
            data-testid={`activity-row-seller-link-${a.alias}`}
          >
            {a.seller_name}
          </Link>
        )}

        {(a.duration || a.location) && (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-600" data-testid={`activity-row-meta-${a.alias}`}>
            {a.duration && <span className="inline-flex items-center gap-1"><Hourglass className="h-3.5 w-3.5 text-slate-400" /> {a.duration}</span>}
            {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {a.location}</span>}
          </p>
        )}

        {description && (
          <p className="line-clamp-2 text-sm text-slate-600" data-testid={`activity-row-description-${a.alias}`}>{description}</p>
        )}

        {(tags.length > 0 || categories.length > 0) && (
          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1" data-testid={`activity-row-tags-${a.alias}`}>
            {categories.map((c) => (
              <span key={`c-${c}`} className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-blue-800">{c}</span>
            ))}
            {tags.map((t) => (
              <span key={`t-${t}`} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                <Tag className="h-2.5 w-2.5" /> {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ---------- price + book ---------- */}
      <div className="flex w-full shrink-0 flex-col items-center justify-center gap-2 border-t border-gray-200 bg-gray-50/60 p-4 sm:w-44 sm:border-l sm:border-t-0">
        {a.price_from ? (
          <div className="text-center" data-testid={`activity-row-price-${a.alias}`}>
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">From</span>
            <span className="text-2xl font-black text-slate-900">${a.price_from}</span>
          </div>
        ) : (
          <span className="text-center text-xs font-bold uppercase tracking-wide text-slate-500">Book Direct</span>
        )}

        <button
          onClick={(e) => onBook(a, e)}
          className="flex w-full items-center justify-center gap-2 rounded-sm bg-blue-800 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-blue-900"
          data-testid={`activity-row-book-now-${a.alias}`}
        >
          {external ? <ExternalLink className="h-4 w-4" /> : <Anchor className="h-4 w-4" />} Book Now
        </button>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleCompare(a); }}
          disabled={!compareSelected && compareFull}
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            compareSelected ? 'text-teal-700' : 'text-slate-500 hover:text-slate-800'
          }`}
          data-testid={`activity-row-compare-${a.alias}`}
        >
          <GitCompare className="h-3.5 w-3.5" /> {compareSelected ? 'Comparing' : 'Compare'}
        </button>
      </div>
    </article>
  );
};

export default ActivityListRow;
